import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SIDEBAR, ALL_PAGES, PAGE_META, DEFAULT_PAGE, PAGE_ALIASES } from './docs/manifest';
import PAGES from './docs';

/**
 * A dokumentáció VÁZA: menü, kereső, morzsa, előző/következő.
 *
 * A tartalom nincs itt — minden doksi-oldal saját fájl a `pages/docs/` alatt,
 * a sorrendjüket és a menüt a `docs/manifest.js` írja le. Egy oldal
 * újraírásához elég azt az egy fájlt kicserélni.
 */

export default function DocsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // A megszűnt id-k (pl. a régi `misc`) az utódjukra mutatnak — lásd manifest.js.
  const requestedPage = searchParams.get('page') || DEFAULT_PAGE;
  const activePage = PAGE_ALIASES[requestedPage] || requestedPage;

  const navigate = (pageId) => {
    setSearchParams({ page: pageId });
    setMobileSidebarOpen(false);
    window.scrollTo({ top: 0 });
  };

  // Filter sidebar items by search query
  const filteredSidebar = query.trim()
    ? SIDEBAR.map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.label.toLowerCase().includes(query.toLowerCase())
        ),
      })).filter(s => s.items.length > 0)
    : SIDEBAR;

  const PageComponent = PAGES[activePage] || PAGES[DEFAULT_PAGE];
  const meta = PAGE_META[activePage] || PAGE_META[DEFAULT_PAGE];

  // Scroll to top when page changes
  useEffect(() => { window.scrollTo({ top: 0 }); }, [activePage]);

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-white/[0.06]">
        <div className="relative">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Search docs…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="input pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
        {filteredSidebar.map(section => (
          <div key={section.title}>
            <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-3 mb-2">
              {section.title}
            </p>
            <ul className="space-y-0.5 border-l border-white/[0.06] ml-3">
              {section.items.map(item => (
                <li key={item.id}>
                  <button
                    onClick={() => navigate(item.id)}
                    className={`w-full text-left -ml-px pl-4 pr-3 py-2 text-sm border-l
                                transition-all duration-150 ${
                      activePage === item.id
                        ? 'border-brand-400 text-brand-200 bg-brand-500/[0.07]'
                        : 'border-transparent text-gray-500 hover:text-white hover:border-white/20'
                    }`}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {filteredSidebar.length === 0 && (
          <p className="text-sm text-gray-600 px-3">No results for "{query}"</p>
        )}
      </nav>

      {/* External links */}
      <div className="border-t border-white/[0.06] p-3">
        <a
          href="https://discord.gg/Frbjdmtbrt"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm
                     text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
        >
          <span className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="#5865F2" width="16" height="16" style={{ flexShrink: 0 }}>
              <path fillRule="evenodd" clipRule="evenodd" d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942.0209-.0414.001-.091-.0417-.1057a13.2019 13.2019 0 01-1.8724-.8936.0767.0767 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
            </svg>
            Discord
          </span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="w-3.5 h-3.5 text-gray-600">
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>
      </div>
    </div>
  );

  return (
    <div className="flex">
      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:flex flex-col w-64 flex-shrink-0 sticky top-16 self-start
                        h-[calc(100vh-64px)] border-r border-white/[0.06] bg-gray-950/60
                        backdrop-blur-xl overflow-hidden">
        <Sidebar />
      </aside>

      {/* ── Mobile sidebar overlay ── */}
      {mobileSidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex animate-fade-in">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"
               onClick={() => setMobileSidebarOpen(false)} />
          <div className="relative z-50 w-72 h-full bg-gray-950 border-r border-white/[0.07] flex flex-col">
            <Sidebar />
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]
                        bg-gray-950/80 backdrop-blur-xl sticky top-16 z-20">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="btn-icon"
            aria-label="Open docs menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <span className="text-sm text-gray-500 truncate">
            {meta.section} <span className="text-gray-700 mx-1">/</span>
            <span className="text-gray-200">{meta.label}</span>
          </span>
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
          {/* Breadcrumb (desktop) */}
          <p className="hidden md:block text-xs font-medium uppercase tracking-wider text-gray-600 mb-2">
            {meta.section}
            <span className="mx-1.5 text-gray-700">/</span>
            <span className="text-brand-300">{meta.label}</span>
          </p>

          <PageComponent navigate={navigate} />

          {/* Prev / Next navigation */}
          <PrevNextNav activePage={activePage} navigate={navigate} />
        </div>
      </div>
    </div>
  );
}

function PrevNextNav({ activePage, navigate }) {
  const allPages = ALL_PAGES;
  const idx = allPages.findIndex(p => p.id === activePage);
  const prev = idx > 0 ? allPages[idx - 1] : null;
  const next = idx < allPages.length - 1 ? allPages[idx + 1] : null;

  if (!prev && !next) return null;

  return (
    <div className="mt-16 pt-6 border-t border-white/[0.06] flex gap-4">
      {prev ? (
        <button
          onClick={() => navigate(prev.id)}
          className="flex-1 card card-interactive px-5 py-4 text-left group"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5
                        flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="w-3 h-3 transition-transform duration-200 group-hover:-translate-x-0.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Previous
          </p>
          <p className="text-sm font-semibold text-white group-hover:text-brand-200 transition-colors">
            {prev.label}
          </p>
        </button>
      ) : <div className="flex-1" />}

      {next ? (
        <button
          onClick={() => navigate(next.id)}
          className="flex-1 card card-interactive px-5 py-4 text-right group"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5
                        flex items-center gap-1.5 justify-end">
            Next
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </p>
          <p className="text-sm font-semibold text-white group-hover:text-brand-200 transition-colors">
            {next.label}
          </p>
        </button>
      ) : <div className="flex-1" />}
    </div>
  );
}
